import type { RealtimeChannel, SupabaseClient } from "@supabase/supabase-js";
import { setRealtimeConnected } from "../store/slices/syncSlice";
import { REALTIME_TABLES, dbName } from "./tableRegistry";
import type { SyncStore } from "./types";

// Coalesce a burst of change events (e.g. a recipe + its ingredients + steps all
// arriving from one remote save) into a single pull shortly after they settle.
const REALTIME_DEBOUNCE_MS = 800;

export interface RealtimeSyncDeps {
  supabase: SupabaseClient;
  store: SyncStore;
  userId: string;
  accessToken: string;
  runSync: () => void | Promise<unknown>;
}

// Subscribe to Postgres change events for the current user's recipe content and
// nudge runSync (debounced) whenever the server reports a change — so edits made
// on another device land in seconds instead of waiting for the fallback poll.
// Framework-agnostic: each app calls this from its own effect and invokes the
// returned cleanup on teardown / auth change / going offline.
//
// We deliberately DON'T apply the event payload directly — runSync's pull already
// does cursor tracking, dirty-aware LWW, image caching and tombstone purge, and
// Realtime can drop/reorder events, so each event is just a "something changed,
// go pull" nudge. Our own pushes echo back as events too; that triggers a pull
// that re-applies identical (non-dirty) rows — harmless and idempotent.
//
// On every (re)connect we fully `await disconnect()` the socket first, THEN
// subscribe — the OS often kills the socket without a clean close, leaving a
// "zombie" that still reports connected while connect() no-ops. Awaiting is
// required because connect() (called by .subscribe()) is skipped while the client
// is still disconnecting. (disconnect() is global, but the app has one channel.)
export function subscribeRealtime({
  supabase,
  store,
  userId,
  accessToken,
  runSync,
}: RealtimeSyncDeps): () => void {
  let channel: RealtimeChannel | null = null;
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  // Start disconnected until the socket confirms SUBSCRIBED (also clears any stale
  // value rehydrated from a previous session).
  store.dispatch(setRealtimeConnected(false));

  const nudge = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      runSync();
    }, REALTIME_DEBOUNCE_MS);
  };

  (async () => {
    // Fully tear down any prior/zombie socket before reconnecting. Must await:
    // connect() is a no-op while the client is still disconnecting.
    await supabase.realtime.disconnect();
    if (cancelled) return;

    // Authenticate the Realtime socket so RLS scopes change events to this user.
    supabase.realtime.setAuth(accessToken);

    channel = supabase.channel(`sync:${userId}`);
    for (const table of REALTIME_TABLES) {
      channel.on(
        "postgres_changes",
        {
          event: "*", // INSERT | UPDATE (soft-deletes are UPDATEs) | DELETE
          schema: "public",
          table: dbName(table),
          filter: `user_id=eq.${userId}`, // scope to this user (RLS also enforces)
        },
        nudge,
      );
    }

    channel.subscribe((status) => {
      const connected = status === "SUBSCRIBED";
      store.dispatch(setRealtimeConnected(connected));
      // On (re)connect, pull once to catch anything that changed while the socket
      // was down — closes the gap Realtime alone can't.
      if (connected) runSync();
    });
  })();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
    store.dispatch(setRealtimeConnected(false));
    if (channel) supabase.removeChannel(channel);
  };
}
